import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getBootstrapData, isOffline } from "@/lib/offline-bootstrap";
import type { Tables } from "@/integrations/supabase/types";

type Restaurant = Tables<"restaurants">;

interface UseRestaurantReturn {
  restaurants: Restaurant[];
  activeRestaurant: Restaurant | null;
  setActiveRestaurant: (restaurant: Restaurant) => void;
  isLoading: boolean;
  needsOnboarding: boolean;
  isOfflineMode: boolean;
  refetch: () => Promise<void>;
}

const ACTIVE_RESTAURANT_KEY = "actualfood_active_restaurant";
const CACHED_RESTAURANTS_KEY = "actualfood_cached_restaurants";

/**
 * Save restaurants to localStorage for offline access
 */
function cacheRestaurants(restaurants: Restaurant[]): void {
  try {
    localStorage.setItem(CACHED_RESTAURANTS_KEY, JSON.stringify(restaurants));
  } catch (error) {
    console.error('[useRestaurant] Error caching restaurants:', error);
  }
}

/**
 * Get cached restaurants from localStorage
 */
function getCachedRestaurants(): Restaurant[] {
  try {
    const cached = localStorage.getItem(CACHED_RESTAURANTS_KEY);
    if (!cached) return [];
    return JSON.parse(cached);
  } catch {
    return [];
  }
}

/**
 * Create a minimal restaurant object for offline mode from bootstrap data
 */
function createMinimalRestaurant(bootstrapData: { restaurantId: string; restaurantName: string; restaurantSlug?: string }): Restaurant {
  return {
    id: bootstrapData.restaurantId,
    name: bootstrapData.restaurantName,
    slug: bootstrapData.restaurantSlug || '',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    is_active: true,
    // All nullable fields with defaults
    logo_url: null,
    phone: null,
    address: null,
    cnpj: null,
    public_menu_enabled: false,
    customer_ordering_enabled: false,
    delivery_enabled: false,
    pickup_enabled: false,
    delivery_fee: null,
    min_order_amount: null,
    delivery_time_estimate: null,
    whatsapp_number: null,
    auto_approve_table_orders: false,
    // Additional fields from schema
    auto_close_period_enabled: null,
    auto_close_period_hours: null,
    bairro: null,
    cep: null,
    city: null,
    codigo_municipio: null,
    complemento_endereco: null,
    couvert_enabled: null,
    couvert_price: null,
    crt: null,
    delivery_auto_reply_enabled: null,
    delivery_greeting_message: null,
    delivery_whatsapp_number: null,
    ie: null,
    low_margin_alert_percentage: null,
    nome_fantasia: null,
    numero_endereco: null,
    partial_payment_enabled: null,
    people_count_checkout_enabled: null,
    print_cancellation_kitchen: null,
    print_cancellation_receipt: null,
    razao_social: null,
    require_command_entry: null,
    require_period_close_after_inactivity: null,
    screensaver_idle_timeout_seconds: null,
    screensaver_interval_seconds: null,
    service_tax_enabled: null,
    service_tax_percentage: null,
    show_pdv_keyboard_shortcuts: null,
    show_period_sales_summary: false,
    show_table_consumption_value: null,
    stock_block_sales: null,
    table_inactivity_minutes: null,
    tablet_mode_enabled: null,
    transfer_pending_consumption: null,
    uf: null,
    whatsapp_notify_cancellation: null,
    whatsapp_notify_discount: null,
    whatsapp_notify_low_stock: null,
    whatsapp_notify_order_confirmed: null,
    whatsapp_notify_order_received: null,
    whatsapp_notify_out_for_delivery: null,
    pdv_require_operator_login: false,
    print_mode: 'qz_tray',
    show_period_totals_on_close: true,
    print_exit_release: false,
  };
}

export function useRestaurant(): UseRestaurantReturn {
  const { user, isOfflineMode: authOfflineMode } = useAuth();
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [activeRestaurant, setActiveRestaurantState] = useState<Restaurant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOfflineMode, setIsOfflineMode] = useState(false);

  const fetchRestaurants = useCallback(async () => {
    if (!user) {
      setRestaurants([]);
      setActiveRestaurantState(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // Check if we're in offline mode
    if (authOfflineMode || isOffline()) {
      console.log('[useRestaurant] Offline mode: Loading from cache');
      
      // Try to load from bootstrap data first
      const bootstrapData = getBootstrapData();
      const cachedRestaurants = getCachedRestaurants();
      
      if (bootstrapData && cachedRestaurants.length > 0) {
        // Find the restaurant from cache that matches bootstrap
        const bootstrapRestaurant = cachedRestaurants.find(
          r => r.id === bootstrapData.restaurantId
        );
        
        if (bootstrapRestaurant) {
          setRestaurants([bootstrapRestaurant]);
          setActiveRestaurantState(bootstrapRestaurant);
          setIsOfflineMode(true);
          setIsLoading(false);
          return;
        }
      }
      
      // Fallback: create minimal restaurant from bootstrap data
      if (bootstrapData) {
        const minimalRestaurant = createMinimalRestaurant(bootstrapData);
        
        setRestaurants([minimalRestaurant]);
        setActiveRestaurantState(minimalRestaurant);
        setIsOfflineMode(true);
        setIsLoading(false);
        return;
      }
      
      // No cache available
      setRestaurants([]);
      setActiveRestaurantState(null);
      setIsOfflineMode(true);
      setIsLoading(false);
      return;
    }

    // Online mode: fetch from Supabase
    try {
      // Buscar restaurantes onde o usuário está vinculado
      const { data: restaurantUsers, error: ruError } = await supabase
        .from("restaurant_users")
        .select("restaurant_id")
        .eq("user_id", user.id);

      if (ruError) throw ruError;

      if (!restaurantUsers || restaurantUsers.length === 0) {
        setRestaurants([]);
        setActiveRestaurantState(null);
        setIsLoading(false);
        setIsOfflineMode(false);
        return;
      }

      const restaurantIds = restaurantUsers.map((ru) => ru.restaurant_id);

      const { data: restaurantsData, error: restError } = await supabase
        .from("restaurants")
        .select("*")
        .in("id", restaurantIds);

      if (restError) throw restError;

      const fetchedRestaurants = restaurantsData || [];
      setRestaurants(fetchedRestaurants);
      
      // Cache restaurants for offline use
      if (fetchedRestaurants.length > 0) {
        cacheRestaurants(fetchedRestaurants);
      }

      // Restaurar restaurante ativo do localStorage
      const savedId = localStorage.getItem(ACTIVE_RESTAURANT_KEY);
      const savedRestaurant = fetchedRestaurants.find((r) => r.id === savedId);

      if (savedRestaurant) {
        setActiveRestaurantState(savedRestaurant);
      } else if (fetchedRestaurants.length > 0) {
        setActiveRestaurantState(fetchedRestaurants[0]);
        localStorage.setItem(ACTIVE_RESTAURANT_KEY, fetchedRestaurants[0].id);
      }
      
      setIsOfflineMode(false);
    } catch (error) {
      console.error("Erro ao buscar restaurantes:", error);
      
      // On error, try to use cached data
      const cachedRestaurants = getCachedRestaurants();
      const bootstrapData = getBootstrapData();
      
      if (cachedRestaurants.length > 0) {
        console.log('[useRestaurant] Network error: Falling back to cache');
        setRestaurants(cachedRestaurants);
        
        const savedId = localStorage.getItem(ACTIVE_RESTAURANT_KEY);
        const savedRestaurant = cachedRestaurants.find((r) => r.id === savedId);
        
        if (savedRestaurant) {
          setActiveRestaurantState(savedRestaurant);
        } else if (cachedRestaurants.length > 0) {
          setActiveRestaurantState(cachedRestaurants[0]);
        }
        
        setIsOfflineMode(true);
      } else if (bootstrapData) {
        // Create minimal restaurant from bootstrap
        const minimalRestaurant = createMinimalRestaurant(bootstrapData);
        
        setRestaurants([minimalRestaurant]);
        setActiveRestaurantState(minimalRestaurant);
        setIsOfflineMode(true);
      } else {
        setRestaurants([]);
        setActiveRestaurantState(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, [user, authOfflineMode]);

  useEffect(() => {
    fetchRestaurants();
  }, [fetchRestaurants]);

  const setActiveRestaurant = useCallback((restaurant: Restaurant) => {
    setActiveRestaurantState(restaurant);
    localStorage.setItem(ACTIVE_RESTAURANT_KEY, restaurant.id);
  }, []);

  return {
    restaurants,
    activeRestaurant,
    setActiveRestaurant,
    isLoading,
    needsOnboarding: !isLoading && restaurants.length === 0 && !isOfflineMode,
    isOfflineMode,
    refetch: fetchRestaurants,
  };
}
